import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';

import {
  AccordionSection,
  AccordionTableViewComponent,
} from '../../common/accordion-table-view/accordion-table-view.component';
import { MenuDropdownComponent } from '../../common/menu-dropdown/menu-dropdown.component';
import { TreeViewComponent } from '../../tree-view/tree-view.component';
import { TreeNode } from '../../../core/models/treeNode';
import { ProjectService } from '../../../core/services/project/project.service';
import { RestService } from '../../../core/services/rest.service';
import { ToasterService } from '../../../core/services/toaster-service';
import { MessageEnum } from '../../../core/utils/message.enum';

@Component({
  selector: 'app-add-cicd-execution-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TreeViewComponent,
    AccordionTableViewComponent,
    MenuDropdownComponent,
  ],
  templateUrl: './add-cicd-execution-modal.component.html',
  styleUrl: './add-cicd-execution-modal.component.scss',
})
export class AddCicdExecutionModalComponent implements OnInit {
  readonly activeModal = inject(NgbActiveModal);
  private readonly restService = inject(RestService);
  private readonly projectService = inject(ProjectService);
  private readonly toaster = inject(ToasterService);

  @Input() configs: any = {};
  @Input() editMode: boolean = false;
  @Input() pipelineId: string = '';
  @Input() initialSections: AccordionSection[] = [];

  // ── IDs ────────────────────────────────────────────────────────────────────
  projectId: string = '';
  automationProjectId: string = '';

  // ── UI state ───────────────────────────────────────────────────────────────
  isLoading: boolean = true;
  isSubmitting: boolean = false;

  // ── Left panel ─────────────────────────────────────────────────────────────
  planNodes: TreeNode[] = [];
  suiteNodes: TreeNode[] = [];
  selectedTreeNode: TreeNode | null = null;
  treeSearch: string = '';

  planSectionExpanded: boolean = true;
  suiteSectionExpanded: boolean = true;

  get suiteCount(): number {
    return this.suiteNodes.length;
  }

  get filteredPlanNodes(): TreeNode[] {
    if (!this.treeSearch.trim()) return this.planNodes;
    const term = this.treeSearch.toLowerCase();
    const result: TreeNode[] = [];
    for (const node of this.planNodes) {
      if (node.displayName?.toLowerCase().includes(term)) {
        result.push(node);
      } else if ((node as any).children?.length) {
        const matchingChildren = (node as any).children.filter(
          (c: any) => c.displayName?.toLowerCase().includes(term),
        );
        if (matchingChildren.length > 0) {
          result.push({ ...(node as any), children: matchingChildren });
        }
      }
    }
    return result;
  }

  get filteredSuiteNodes(): TreeNode[] {
    if (!this.treeSearch.trim()) return this.suiteNodes;
    const term = this.treeSearch.toLowerCase();
    const result: TreeNode[] = [];
    for (const node of this.suiteNodes) {
      if (node.displayName?.toLowerCase().includes(term)) {
        result.push(node);
      } else if ((node as any).children?.length) {
        const matchingChildren = (node as any).children.filter(
          (c: any) => c.displayName?.toLowerCase().includes(term),
        );
        if (matchingChildren.length > 0) {
          result.push({ ...(node as any), children: matchingChildren });
        }
      }
    }
    return result;
  }

  /** IDs of nodes that have already been moved to the right panel. */
  private readonly addedNodeIds = new Set<string>();

  // ── Right panel ────────────────────────────────────────────────────────────
  accordionSections: AccordionSection[] = [];
  paginationInfo: any = { currentStart: 1, currentEnd: 0, total: 0, label: '' };

  // ── "Set as Default" toolbar ───────────────────────────────────────────────
  isDefaultEnabled: boolean = false;
  globalAgentId: string | null = null;
  globalBrowser: string | null = null;
  executionEngineOptions: any[] = [];
  executionEngineTableOptions: any[] = [];

  readonly browserOptions = [
    { label: 'Chrome',  value: 'Chrome',  image: 'assets/images/virtusa-icons/chrome.svg' },
    { label: 'Firefox', value: 'Firefox', image: 'assets/images/virtusa-icons/firefox.svg' },
    { label: 'Edge',    value: 'Edge',    image: 'assets/images/virtusa-icons/edge.svg' },
  ];

  // ── Pagination ─────────────────────────────────────────────────────────────
  readonly pageSize = 10;
  private readonly currentPage = 1;

  get totalTestCases(): number {
    return this.accordionSections.reduce(
      (sum, s) => sum + (s.tableRows?.length ?? 0), 0,
    );
  }

  get rangeLabel(): string {
    const total = this.accordionSections.length;
    if (total === 0) return '0 - 0 of 0';
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, total);
    return `${start} - ${end} of ${total}`;
  }

  // ── Arrow button guards ────────────────────────────────────────────────────
  get canMoveRight(): boolean {
    if (!this.selectedTreeNode || !(this.selectedTreeNode as any).dragable) return false;
    const node = this.selectedTreeNode as any;
    // Plans can always be added again (duplicates allowed)
    if (node.sourceType === 'plan') return true;
    // Test cases/suites can only be added once
    return !this.addedNodeIds.has(node.id);
  }

  get canMoveLeft(): boolean {
    if (!this.selectedTreeNode) return false;
    const node = this.selectedTreeNode as any;
    // For plans: allow removal if at least one instance exists on the right
    if (node.sourceType === 'plan') {
      return this.accordionSections.some(s => (s as any)._nodeId === node.id);
    }
    return this.addedNodeIds.has(node.id);
  }

  // ──────────────────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.projectService.getProjectId().subscribe(id => { if (id) this.projectId = id; });
    this.projectService
      .getAutomationProjectId()
      .subscribe((id: string | null) => { if (id) this.automationProjectId = id; });

    // Small delay lets project IDs resolve before the API calls
    setTimeout(() => this.loadData(), 0);
  }

  // ── Data loading ───────────────────────────────────────────────────────────
  private loadData(): void {
    this.isLoading = true;

    forkJoin({
      plans: this.restService.getTestPlansWithVersions(
        this.projectId,
        this.automationProjectId,
      ),
      scripts: this.restService.getTestSuitsAndTestCases(
        this.projectId,
        this.automationProjectId,
      ),
      agents: this.restService.getAgentsWithStatus(this.projectId),
      devices: this.restService.getDevices(this.projectId),
    }).subscribe({
      next: ({ plans, scripts, agents, devices }) => {
        this.buildExecutionEngineOptions(agents?.data ?? [], devices?.data ?? []);
        this.buildTreeNodes(plans?.data ?? [], scripts?.data ?? []);
        if (this.editMode && this.initialSections.length > 0) {
          this.accordionSections = this.initialSections.map(s => ({
            ...s,
            tableColumns: this.buildTableColumns(),
            expanded: true,
          }));
          this.accordionSections.forEach(s => {
            if (s.id) this.addedNodeIds.add(s.id as string);
          });
          this.updatePaginationInfo();
        }
        this.isLoading = false;
      },
      error: () => {
        this.toaster.pop({ type: 'error', text: MessageEnum.TEST_CASE_LOAD_FAILURE });
        this.isLoading = false;
      },
    });
  }

  private buildExecutionEngineOptions(agents: any[], devices: any[]): void {
    const localAgents: any[] = [];
    const cloudAgents: any[] = [];
    const localDevices: any[] = [];
    const remoteDevices: any[] = [];

    agents
      .filter((a: any) => a.active)
      .forEach((agent: any) => {
        const opt = {
          label: agent.name,
          icon:
            agent.status === 'OFFLINE'
              ? 'assets/images/agent/agent-dead.svg'
              : 'assets/images/agent/agent-alive.svg',
          value: agent.id,
          agentType: agent.type,
        };
        agent.type === 'LOCAL' ? localAgents.push(opt) : cloudAgents.push(opt);
      });

    devices.forEach((device: any) => {
      const opt = { label: device.name, value: device.id, deviceType: device.environment };
      device.environment?.toUpperCase() === 'LOCAL'
        ? localDevices.push(opt)
        : remoteDevices.push(opt);
    });

    const engineSections = [
      {
        label: 'Execution Engines',
        type: 'agents',
        options: [
          {
            label: 'On Demand',
            options: [{ label: 'On Demand', icon: 'assets/images/agent/agent-alive.svg', value: 'onDemand', agentType: 'CLUSTER' }],
          },
          ...(localAgents.length ? [{ label: 'Local', options: localAgents }] : []),
          ...(cloudAgents.length ? [{ label: 'Cloud', options: cloudAgents }] : []),
        ],
      },
      {
        label: 'Devices',
        type: 'devices',
        options: [
          ...(localDevices.length ? [{ label: 'Local', options: localDevices }] : []),
          ...(remoteDevices.length ? [{ label: 'Remote', options: remoteDevices }] : []),
        ],
      },
    ];

    this.executionEngineOptions = engineSections;
    this.executionEngineTableOptions = engineSections;
  }

  private buildTreeNodes(plansData: any[], scriptsData: any[]): void {
    this.planNodes = (plansData ?? []).map((plan: any) => ({
      id: `${plan.id}`,
      name: plan.name,
      displayName: plan.name,
      order: 0,
      level: 0,
      active: plan.active,
      icon: 'folder',
      dragable: true,
      sourceType: 'plan',
      sourceId: plan.id,
      testScripts: plan.testScripts ?? [],
      children: (plan.testScripts ?? []).map((tc: any, idx: number) => ({
        id: `${tc.id}`,
        name: tc.name,
        displayName: tc.name,
        order: idx,
        level: 1,
        active: true,
        icon: 'draft',
        dragable: false,
        sourceType: 'plan-case',
      })),
    } as any)) as TreeNode[];

    const scripts = (scriptsData ?? []).filter((i: any) => i.type === 'SCRIPT');
    const suiteMap = new Map<string, any>();
    const ungrouped: any[] = [];

    scripts.forEach((item: any) => {
      if (item.automationScriptGroup) {
        const gid = item.automationScriptGroup.id;
        if (!suiteMap.has(gid)) {
          suiteMap.set(gid, {
            id: `suite-${gid}`,
            name: item.automationScriptGroup.name,
            displayName: item.automationScriptGroup.name,
            order: 0,
            level: 0,
            active: true,
            icon: 'folder',
            dragable: false,
            sourceType: 'suite',
            sourceId: gid,
            children: [],
          });
        }
        const node = suiteMap.get(gid);
        node.children.push({
          id: `suite-tc-${item.id}`,
          name: item.name,
          displayName: item.name,
          order: 0,
          level: 1,
          active: item.active,
          icon: 'draft',
          dragable: true,
          sourceType: 'case',
          sourceId: item.id,
          rawItems: [item],
        });
      } else {
        ungrouped.push(item);
      }
    });

    const ungroupedNodes: any[] = ungrouped.map((item: any) => ({
      id: `case-${item.id}`,
      name: item.name,
      displayName: item.name,
      order: 0,
      level: 0,
      active: item.active,
      icon: 'draft',
      dragable: true,
      sourceType: 'case',
      sourceId: item.id,
      rawItems: [item],
      children: undefined,
    }));

    this.suiteNodes = [...suiteMap.values(), ...ungroupedNodes] as TreeNode[];
  }

  // ── Tree events ────────────────────────────────────────────────────────────
  onTreeNodeSelect(node: TreeNode): void {
    if ((node as any).dragable) {
      this.selectedTreeNode = node;
    }
  }


  private readonly ADHOC_SECTION_ID = 'adhoc-test-plan';
  private readonly ADHOC_SECTION_TITLE = 'AdHocTestPlan';

  // ── Move right (add to selected panel) ────────────────────────────────────
  moveToRight(): void {
    if (!this.canMoveRight || !this.selectedTreeNode) return;

    const node = this.selectedTreeNode as any;

    if (node.sourceType === 'plan') {
      // Plans get their own dedicated accordion section; duplicates are allowed
      const section = this.buildPlanAccordionSection(node);
      if (!section) return;
      this.accordionSections = [...this.accordionSections, section];
      // Don't track in addedNodeIds — plans may be added multiple times
    } else {
      // Suite children / standalone test cases → merge into AdHocTestPlan
      const newRows = this.buildRowsForNode(node);
      const existingIdx = this.accordionSections.findIndex(
        s => s.id === this.ADHOC_SECTION_ID,
      );

      if (existingIdx >= 0) {
        const updated = { ...this.accordionSections[existingIdx] };
        updated.tableRows = [...(updated.tableRows ?? []), ...newRows];
        updated.itemCount = updated.tableRows.length;
        updated.testcaseCount = updated.tableRows.length;
        const sections = [...this.accordionSections];
        sections[existingIdx] = updated;
        this.accordionSections = sections;
      } else {
        const adhocSection: AccordionSection = {
          id: this.ADHOC_SECTION_ID,
          title: this.ADHOC_SECTION_TITLE,
          testSuiteName: this.ADHOC_SECTION_TITLE,
          itemCount: newRows.length,
          testcaseCount: newRows.length,
          testSuiteId: this.ADHOC_SECTION_ID,
          expanded: true,
          tableColumns: this.buildTableColumns(),
          tableRows: newRows,
        };
        this.accordionSections = [...this.accordionSections, adhocSection];
      }
    }

    // Only track non-plan nodes (test cases/suites) to prevent duplicate AdHoc rows
    if (node.sourceType !== 'plan') {
      this.addedNodeIds.add(node.id);
    }
    this.updatePaginationInfo();
  }

  private buildPlanAccordionSection(node: any): AccordionSection | null {
    const rows = (node.testScripts ?? []).map((tc: any) => ({
      testCaseId: tc.id,
      scriptId: tc.scriptId,
      testCaseName: tc.name,
      version: tc.version ?? null,
      increment: tc.increment ?? 0,
      versionOptions: (tc.versions ?? []).map((v: any) => v.version),
      executionEngine: tc.agentId ?? null,
      browser: null,
      browserOptions: this.browserOptions.map(o => o.value),
      tcgTestCaseResultId: tc.tcgTestCaseResultId ?? null,
      select: false,
    }));

    return {
      id: `${node.id}-${Date.now()}`,
      _nodeId: node.id,
      title: node.name,
      testSuiteName: node.name,
      itemCount: rows.length,
      testcaseCount: rows.length,
      testSuiteId: node.id,
      expanded: false,
      tableColumns: this.buildTableColumns(),
      tableRows: rows,
    } as any;
  }

  /** Build table rows for a suite-child or standalone test-case node. */
  private buildRowsForNode(node: any): any[] {
    const rawItems: any[] = node.rawItems ?? [];
    if (rawItems.length > 0) {
      return rawItems.map((item: any) => ({
        testCaseId: item.id,
        scriptId: item.scriptId,
        testCaseName: item.name,
        version: item.version ?? null,
        increment: item.increment ?? 0,
        versionOptions: (item.versions ?? []).map((v: any) => v.version),
        executionEngine: item.agentId ?? null,
        browser: null,
        browserOptions: this.browserOptions.map(o => o.value),
        tcgTestCaseResultId: item.tcgTestCaseResultId ?? null,
        select: false,
        _nodeId: node.id,
      }));
    }
    // Fallback for a node with no rawItems
    return [{
      testCaseId: node.sourceId,
      scriptId: node.scriptId ?? null,
      testCaseName: node.name,
      version: null,
      increment: 0,
      versionOptions: [],
      executionEngine: null,
      browser: null,
      browserOptions: this.browserOptions.map(o => o.value),
      tcgTestCaseResultId: null,
      select: false,
      _nodeId: node.id,
    }];
  }

  private buildTableColumns(): any[] {
    return [
      {
        display_name: '',
        key: 'select',
        headerStyles: { width: '5%' },
        _config: { type: 'checkbox', displayInColumn: true },
      },
      {
        display_name: 'Test Case Name',
        key: 'testCaseName',
        headerStyles: { width: '35%' },
        cellStyles: { 'word-break': 'break-word', 'white-space': 'normal', 'max-width': '0', 'overflow-wrap': 'break-word' },
      },
      {
        display_name: 'Version',
        key: 'version',
        headerStyles: { width: '20%' },
        _config: {
          type: 'multi-select',
          displayInColumn: false,
          options: [],
          placeholder: 'Select version',
        },
      },
      {
        display_name: 'Execution Engine/ Device',
        key: 'executionEngine',
        headerStyles: { width: '28%' },
        _config: {
          type: 'menu_dropdown',
          targetKey: 'executionEngine',
          componentInputs: { fields: this.executionEngineTableOptions },
          placeholder: 'Select',
        },
      },
      {
        display_name: 'Browser',
        key: 'browser',
        headerStyles: { width: '12%' },
        _config: {
          type: 'browser-select',
          optionsKey: 'browserOptions',
          imageMap: {
            Chrome: 'assets/images/virtusa-icons/chrome.svg',
            Firefox: 'assets/images/virtusa-icons/firefox.svg',
            Edge: 'assets/images/virtusa-icons/edge.svg',
          },
        },
      },
    ];
  }

  // ── Move left (remove from selected panel) ─────────────────────────────────
  moveToLeft(): void {
    if (!this.canMoveLeft || !this.selectedTreeNode) return;

    const node = this.selectedTreeNode as any;
    const nodeId = node.id;

    if (node.sourceType === 'plan') {
      // Remove only the last instance of this plan (most recently added)
      const lastIdx = [...this.accordionSections].map(s => (s as any)._nodeId).lastIndexOf(node.id);
      if (lastIdx >= 0) {
        const sections = [...this.accordionSections];
        sections.splice(lastIdx, 1);
        this.accordionSections = sections;
      }
    } else {
      // Remove only this node's rows from the AdHoc section
      const existingIdx = this.accordionSections.findIndex(
        s => s.id === this.ADHOC_SECTION_ID,
      );
      if (existingIdx >= 0) {
        const updated = { ...this.accordionSections[existingIdx] };
        updated.tableRows = (updated.tableRows ?? []).filter(
          (r: any) => r._nodeId !== nodeId,
        );
        updated.itemCount = updated.tableRows.length;
        updated.testcaseCount = updated.tableRows.length;

        if (updated.tableRows.length === 0) {
          // AdHoc section is now empty — remove it entirely
          this.accordionSections = this.accordionSections.filter(
            s => s.id !== this.ADHOC_SECTION_ID,
          );
        } else {
          const sections = [...this.accordionSections];
          sections[existingIdx] = updated;
          this.accordionSections = sections;
        }
      }
    }

    // Only manage addedNodeIds for non-plan nodes
    if (node.sourceType !== 'plan') {
      this.addedNodeIds.delete(nodeId);
    }
    this.updatePaginationInfo();
  }

  private updatePaginationInfo(): void {
    const total = this.accordionSections.length;
    this.paginationInfo = {
      currentStart: total > 0 ? 1 : 0,
      currentEnd: Math.min(this.pageSize, total),
      total,
      label: 'items',
    };
  }

  // ── Right-panel accordion events ───────────────────────────────────────────
  onSwitchToggle(enabled: boolean): void {
    this.isDefaultEnabled = enabled;
    this.applyGlobalDefaults();
  }

  onExecutionEngineChange(value: any): void {
    this.globalAgentId = value?.id || value?.value || value || null;
    this.applyGlobalDefaults();
  }

  onBrowserChange(value: any): void {
    this.globalBrowser = value?.id || value?.value || value || null;
    this.applyGlobalDefaults();
  }

  private applyGlobalDefaults(): void {
    if (!this.isDefaultEnabled) return;

    this.accordionSections.forEach(section => {
      let changed = false;
      section.tableRows?.forEach((row: any) => {
        if (this.globalAgentId !== null) { row.executionEngine = this.globalAgentId; changed = true; }
        if (this.globalBrowser !== null)  { row.browser = this.globalBrowser; changed = true; }
      });
      if (changed && section.tableRows) {
        section.tableRows = [...section.tableRows];
      }
    });
  }

  onTableActions(event: { section: AccordionSection; action: any }): void {
    const { type, data, value } = event.action ?? {};
    const row = data?.row;
    const key = data?.column?.key;
    if (!row) return;

    if (['multi-select', 'section_dropdown', 'menu_dropdown'].includes(type) && key === 'executionEngine') {
      row.executionEngine = value?.value ?? row.executionEngine ?? value ?? null;
    } else if (type === 'browser-select' && key === 'browser') {
      row.browser = value ?? row.browser ?? null;
    } else if (type === 'multi-select' && key === 'version') {
      row.version = row.version ?? value ?? null;
    }
  }

  // ── Footer actions ─────────────────────────────────────────────────────────
  onNext(): void {
    if (this.accordionSections.length === 0) {
      this.toaster.pop({ type: 'warning', text: MessageEnum.SELECT_AT_LEAST_ONE_TEST_PLAN_OR_SUITE });
      return;
    }
    this.activeModal.close({ step: 1, sections: this.accordionSections, editMode: this.editMode, pipelineId: this.pipelineId });
  }

  onCancel(): void {
    this.activeModal.dismiss('Cross click');
  }
}
